import { useState, useCallback } from "react";
import { omit } from "lodash";
import {
  AccountDetailsMin,
  PayloadAccountDetailsMin,
  useCreateAccountMutation,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { accountTypeMap } from "../../types";
import { formatDate } from "../../../../utils";
import { accountCashbackPayload } from "../../../../utils/cashback";
import { PanelState } from "../types";
import { UseAccountPanelReturn, UseAccountPanelParams } from "./types";

/**
 * Custom hook to manage account panel state and submission
 *
 * @param params - Callbacks for panel actions
 * @returns Panel state and handlers
 */
export const useAccountPanel = ({
  onSuccess,
}: UseAccountPanelParams = {}): UseAccountPanelReturn => {
  const [panel, setPanel] = useState<PanelState>({
    action: "create",
    isOpen: false,
  });
  const [createAccount] = useCreateAccountMutation();

  const openPanel = useCallback(() => {
    setPanel({ action: "create", isOpen: true });
  }, []);

  const closePanel = useCallback(() => {
    setPanel({ action: "create", isOpen: false });
  }, []);

  const handleSubmit = useCallback(
    async (data: AccountDetailsMin) => {
      try {
        const payload: PayloadAccountDetailsMin = {
          ...omit(data, "cashbackMode", "cashbackRate", "cashbackMonthlyCap"),
          initialBalance: Number.parseFloat(data.initialBalance),
          startDate: formatDate(data.startDate),
          accountType: accountTypeMap[data.accountType.value],
          ...accountCashbackPayload(
            accountTypeMap[data.accountType.value],
            data,
          ),
        };

        await createAccount(payload).unwrap();

        notify.success("Account created successfully!");

        closePanel();
        onSuccess?.();
      } catch {
        // The error toast is raised globally by errorToastMiddleware. Swallow
        // here so the panel stays open (closePanel above is skipped) for retry.
      }
    },
    [createAccount, closePanel, onSuccess],
  );

  return {
    panel,
    openPanel,
    closePanel,
    handleSubmit,
  };
};
